import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Stack,
  IconButton,
  Icon,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, ArrowForward, CloudDownload } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import MDButton from "components/MDButton";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { buttonStyles } from "services/utils";
import RequisitionDetailsData from "../RequisitionDetailsData";
import { getJobForm } from "services/api";
import { getJobFormQuestions, getJobFormPageData } from "services/api";
import FormCreationPage from "../FormCreationPage";
import ApplicantsPage from "../ApplicantsPage";
import { saveJobRequisitions } from "services/api";
import { exportRequisitionDetails } from "services/api";
import { exportRequisitionCVs } from "services/api";
import MeetingsPage from "../MeetingsPage";

const RequisitionDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // const { allEmps } = useAuth();
  const { employee, allEmps, jobs } = useAuth();
  const navigate = useNavigate();
  const [exportLoading, setExportLoading] = useState(false);
  const requisitionId = location.state?.requisitionId;
  const [jobRequisitions, setJobRequisitions] = useState(location.state?.jobRequisitions || []);
  const [view, setView] = useState(0);
  const [form, setForm] = useState();
  const [formQuestions, setFormQuestions] = useState();
  const [exportAnchor, setExportAnchor] = useState(null);

  const openExportMenu = (e) => setExportAnchor(e.currentTarget);
  const closeExportMenu = () => setExportAnchor(null);
  const isAdminOrCeo = location.state?.adminOrCeo;

  // Current index
  const currentIndex = jobRequisitions.findIndex((r) => r.id === requisitionId);

  const selectedReq = jobRequisitions[currentIndex];
  const canEdit =
    location.state?.canEdit &&
    selectedReq?.status != "COMPLETED" &&
    selectedReq?.status != "REJECTED";

  // useEffect(() => {
  //   const fetchData = async () => {
  //     const data = await getJobFormPageData(requisitionId);
  //     const fd = await getJobForm(requisitionId);
  //     if (fd != "error") {
  //       setForm(fd);
  //       const fqd = await getJobFormQuestions(fd?.id);
  //       if (fqd != "error") {
  //         setFormQuestions(fqd);
  //       }
  //     }
  //   };
  //   fetchData();
  // }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getJobFormPageData(requisitionId);

        if (data !== "error" && data) {
          setForm(data.form || null);
          setFormQuestions(data.questions || []);
        }
      } catch (error) {
        console.error("Failed to fetch job form page data", error);
      }
    };

    if (requisitionId) {
      fetchData();
    }
  }, [requisitionId]);

  const handleSaveChanges = async (updatedReqObject) => {
    const oldRequisitions = jobRequisitions;
    // 2. Update local state
    setJobRequisitions((prev) =>
      prev.map((r) => (r.id === updatedReqObject.id ? updatedReqObject : r))
    );

    const res = await saveJobRequisitions(updatedReqObject);
    if (res == "error") setJobRequisitions(oldRequisitions);
  };

  const handleStatusChange = async (newStatus) => {
    const reqToUpdate = jobRequisitions.find((r) => r.id === requisitionId);
    if (!reqToUpdate) return;

    const updatedReq = { ...reqToUpdate, status: newStatus };

    // API Call
    await saveJobRequisitions(updatedReq);

    // State Update
    setJobRequisitions((prev) => prev.map((r) => (r.id === requisitionId ? updatedReq : r)));
  };

  if (!selectedReq) return <Typography>{t("admin.requisitionDetails.notFound")}</Typography>;

  // Get employee names
  const hrApprover = allEmps?.find((e) => e.id === selectedReq.hrApprovedBy);
  const directorApprover = allEmps?.find((e) => e.id === selectedReq.directorApprovedBy);

  const allowedStatuses = ["APPROVED", "COMPLETED", "REJECTED"];
  const canShowApplicantAndForm = allowedStatuses.includes(selectedReq?.status);

  const buttons = [
    { label: t("admin.talentAcquisition.buttons.details"), viewIndex: 0 },
    ...(canShowApplicantAndForm
      ? [
          { label: t("admin.talentAcquisition.buttons.applicants"), viewIndex: 1 },
          { label: t("admin.talentAcquisition.buttons.form"), viewIndex: 2 },
          { label: t("admin.talentAcquisition.buttons.meetings"), viewIndex: 3 },
        ]
      : []),
  ];

  return (
    <Box>
      {/* HEADER */}

      <Stack direction="row" justifyContent="flex-start" alignItems="center" mb={3}>
        {/* BACK ARROW BUTTON */}
        <IconButton color="secondary" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>

        {/* PAGE TITLE */}
        <Typography variant="h5" fontWeight={700}>
          {t("admin.talentAcquisition.detailsPageTitle")}
        </Typography>
      </Stack>
      <Stack pb={2}>
        <Grid
          container
          spacing={1}
          alignItems="center"
          sx={{
            justifyContent: "space-between", // left buttons group vs right export button
          }}
        >
          {/* LEFT BUTTONS */}
          <Grid item>
            <Grid container spacing={1}>
              {buttons.map(({ label, viewIndex }) => (
                <Grid item xs={12} sm={6} md="auto" key={viewIndex}>
                  <Button
                    sx={{
                      ...buttonStyles(view === viewIndex),
                      width: { xs: "100%", md: "auto" },
                    }}
                    variant={view === viewIndex ? "contained" : "outlined"}
                    onClick={() => setView(viewIndex)}
                    color="primary"
                  >
                    {label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item>
            <MDButton
              variant="contained"
              color="primary"
              onClick={openExportMenu}
              disabled={exportLoading}
              startIcon={<CloudDownload />}
            >
              {!exportLoading ? t("common.export") : <CircularProgress size={20} color="inherit" />}
            </MDButton>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={closeExportMenu}>
              <MenuItem
                onClick={async () => {
                  closeExportMenu();
                  setExportLoading(true);
                  await exportRequisitionDetails(requisitionId, selectedReq.id);
                  setExportLoading(false);
                }}
              >
                {t("admin.talentAcquisition.buttons.exportApplicantsData")}
              </MenuItem>

              <MenuItem
                onClick={async () => {
                  closeExportMenu();
                  setExportLoading(true);
                  await exportRequisitionCVs(requisitionId, selectedReq.id);
                  setExportLoading(false);
                  // call export CVs function here
                }}
              >
                {t("admin.talentAcquisition.buttons.exportAllCvs")}
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Stack>
      {view == 0 ? (
        <RequisitionDetailsData
          selectedReq={selectedReq}
          hrApprover={hrApprover}
          directorApprover={directorApprover}
          onSaveChanges={handleSaveChanges}
          onStatusChange={handleStatusChange}
          canEdit={canEdit}
        />
      ) : view == 1 ? (
        <ApplicantsPage
          form={form}
          formQuestions={formQuestions}
          canEdit={canEdit}
          empRequestedBy={selectedReq?.requestedBy}
        />
      ) : view === 2 ? (
        <FormCreationPage
          form={form}
          formQuestions={formQuestions}
          setFormQuestions={setFormQuestions}
          setForm={setForm}
          canEdit={canEdit}
          status={selectedReq?.status}
        />
      ) : (
        <MeetingsPage
          form={form}
          canEdit={canEdit}
          adminOrCeo={isAdminOrCeo}
          employeedLoggedId={employee.id}
          isFormView={true}
        />
      )}
    </Box>
  );
};

export default RequisitionDetails;
